import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages as pages } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';
import { CtaButton } from './CtaButton';
import { SamplePage } from './SamplePage';
type Position = {
  active: number;
  prev: number;
};
const AUTOPLAY_MS = 4000;
const CAPTION_SPACE = 96;
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
export function PageCarousel() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const suppressClick = useRef(false);
  const reduceMotion = useReducedMotion();
  const [width, setWidth] = useState(360);
  const [viewportH, setViewportH] = useState(800);
  const [pos, setPos] = useState<Position>({
    active: 0,
    prev: 0
  });
  const [dragX, setDragX] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [touching, setTouching] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const count = pages.length;
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      setWidth(el.clientWidth);
      setViewportH(window.innerHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  const goTo = useCallback((index: number) => setPos(p => ({
    active: (index % count + count) % count,
    prev: p.active
  })), [count]);
  const step = useCallback((dir: 1 | -1) => setPos(p => ({
    active: (p.active + dir + count) % count,
    prev: p.active
  })), [count]);

  // Autoplay: pauses on hover/touch, stops for good once the user interacts.
  useEffect(() => {
    if (stopped || hovered || touching || lightboxIndex !== null || reduceMotion) return;
    const timer = window.setInterval(() => step(1), AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [stopped, hovered, touching, lightboxIndex, reduceMotion, step]);
  const interact = (fn: () => void) => {
    setStopped(true);
    fn();
  };

  // Layout per breakpoint (based on the carousel's own width)
  const isDesktop = width >= 860;
  const isTablet = width >= 520 && !isDesktop;
  const gap = isDesktop ? 28 : isTablet ? 20 : 16;
  // Volume pages are portrait (9:16): narrow, so more pages are on screen at once.
  const targetW = isDesktop ? Math.min(320, Math.max(260, Math.round(width * 0.28))) : isTablet ? Math.round(width * 0.42) : Math.round(width * 0.72);
  // Keep the whole page under ~78vh so it fits on screen without scrolling.
  const maxWByHeight = Math.floor(viewportH * 0.78 * 9 / 16);
  const slideW = Math.max(160, Math.min(targetW, maxWByHeight));
  const imgH = Math.round(slideW * 16 / 9);
  const maxOffset = isDesktop ? 2 : 1;
  const scaleFor = (abs: number) => abs === 0 ? 1 : abs === 1 ? 0.9 : 0.8;
  const opacityFor = (abs: number) => abs <= 1 ? 1 : 0.6;
  // Horizontal centre of each offset, accounting for the scaled widths of neighbours.
  const x1 = slideW * (1 + scaleFor(1)) / 2 + gap;
  const x2Natural = x1 + slideW * (scaleFor(1) + scaleFor(2)) / 2 + gap;
  const x2Fit = width / 2 - slideW * scaleFor(2) / 2;
  const x2 = Math.max(x1 + slideW * 0.3, Math.min(x2Natural, x2Fit));
  const xFor = (offset: number) => {
    const abs = Math.abs(offset);
    const base = abs === 0 ? 0 : abs === 1 ? x1 : abs === 2 ? x2 : x2 + (abs - 2) * (slideW + gap);
    return Math.sign(offset) * base;
  };
  const trackH = imgH + CAPTION_SPACE;
  const circular = (i: number, from: number) => {
    let d = i - from;
    if (d > count / 2) d -= count;
    if (d < -count / 2) d += count;
    return d;
  };

  // Swipe
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.pointerType !== 'mouse') setTouching(true);
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      moved: false
    };
    suppressClick.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    if (!drag.current.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) drag.current.moved = true;
    if (drag.current.moved) setDragX(dx);
  };
  const endDrag = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') setTouching(false);
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const moved = drag.current.moved;
    drag.current = null;
    setDragX(0);
    if (!moved) return;
    suppressClick.current = true;
    if (Math.abs(dx) > 50) interact(() => step(dx < 0 ? 1 : -1));
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      interact(() => step(1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      interact(() => step(-1));
    }
  };
  const openPage = (i: number) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    interact(() => {
      goTo(i);
      setLightboxIndex(i);
    });
  };
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const changeLightbox = useCallback((i: number) => {
    setLightboxIndex(i);
    goTo(i);
  }, [goTo]);
  const arrowClass = 'flex h-12 w-12 flex-none items-center justify-center rounded-full bg-hl text-hl-ink shadow-card transition-colors duration-150 hover:bg-hl-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-chalk/70';
  const prevButton = (className: string) => <button type="button" onClick={() => interact(() => step(-1))} aria-label="Previous page" className={`${arrowClass} ${className}`} data-id="element-221" data-mp="1yo0tddqalooa">
      <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-222" data-mp="2hlanq2bea14w" />
    </button>;
  const nextButton = (className: string) => <button type="button" onClick={() => interact(() => step(1))} aria-label="Next page" className={`${arrowClass} ${className}`} data-id="element-223" data-mp="2miuajqm5rm8g">
      <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-224" data-mp="2ctj8up1lpjul" />
    </button>;
  return <section aria-labelledby="pages-heading" className="lane-band w-full overflow-hidden bg-navy" data-id="element-225" data-mp="2cr6h5k61jv4c">
      <div className="mx-auto max-w-6xl py-16 sm:py-20" data-id="element-226" data-mp="1yw4d72kr8fu6">
        <div className="px-5 text-center" data-id="element-227" data-mp="23tnqo09mool2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-hl" data-id="element-228" data-mp="1pt38957mkus3">Look inside · Volume 1</p>
          <h2 id="pages-heading" className="mt-3 font-display text-3xl text-chalk sm:text-5xl" data-id="element-229" data-mp="1tuf0lihvvu3z">
            Every practice, planned and ready to post
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-chalk/75" data-id="element-230" data-mp="3k7w517k8vrlm">
            Real pages from Volume 1, Day One Ready. Print one, post it on the fence, run practice: the setup diagram,
            the timed blocks, the main set for every group, the cues, and the Level Dial for your age group.
          </p>
        </div>

        <div role="region" aria-roledescription="carousel" aria-label="Pages from Volume 1" tabIndex={0} onKeyDown={onKeyDown} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className="mt-10 flex items-center gap-4 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hl sm:mt-12 lg:px-5" data-id="element-231" data-mp="1mb5txx0ri9im">
          {prevButton('hidden lg:flex')}

          <div ref={viewportRef} className="relative min-w-0 flex-1 touch-pan-y select-none" style={{
          height: trackH
        }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onPointerLeave={e => drag.current && endDrag(e)} data-id="element-232" data-mp="257xfye7ov2qd">
            <p className="sr-only" aria-live={stopped ? 'polite' : 'off'} data-id="element-233" data-mp="3h6dq75lz89s2">
              Page {pos.active + 1} of {count}: {pages[pos.active].caption}
            </p>
            {pages.map((page, i) => {
            const offset = circular(i, pos.active);
            const prevOffset = circular(i, pos.prev);
            const teleport = Math.abs(offset - prevOffset) > 2;
            const isActive = offset === 0;
            const abs = Math.abs(offset);
            const visible = abs <= maxOffset;
            const scale = scaleFor(abs);
            const x = xFor(offset) + dragX;
            const animate = !teleport && dragX === 0 && !reduceMotion;
            return <div key={page.sample} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} aria-hidden={!visible} className="absolute left-1/2 top-0" style={{
              width: slideW,
              transform: `translateX(calc(-50% + ${x}px)) scale(${scale})`,
              transformOrigin: 'top center',
              opacity: visible ? opacityFor(abs) : 0,
              zIndex: 10 - abs,
              transition: animate ? `transform 300ms ${EASE}, opacity 250ms ${EASE}` : 'none',
              pointerEvents: visible ? 'auto' : 'none'
            }} data-id="element-234" data-mp="3c8ucc6t6xz0n" data-mpi={i}>
                  <button type="button" tabIndex={visible ? 0 : -1} onClick={() => openPage(i)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-xl bg-chalk text-left shadow-book focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hl focus-visible:ring-offset-2 focus-visible:ring-offset-navy sm:rounded-2xl" data-id="element-235" data-mp="1o8yuazn7pp4j">
                    {page.src ? <img src={page.src} alt={page.alt} width={1080} height={1920} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} className="block w-full object-contain" style={{
                  height: imgH
                }} data-id="element-236" data-mp="138y60g0qwk4h" /> : <div role="img" aria-label={page.alt} className="pointer-events-none" data-id="element-237" data-mp="fi276yxvgonq">
                        <SamplePage id={page.sample} data-id="element-238" data-mp="u6otxlptwioz" />
                      </div>}
                  </button>
                  <p className={`mx-auto mt-4 max-w-[40ch] text-center text-sm leading-relaxed transition-colors duration-200 ${isActive ? 'text-chalk' : 'text-chalk/50'}`} data-id="element-239" data-mp="peotls5ujnrv">
                    {page.caption}
                  </p>
                </div>;
          })}
          </div>

          {nextButton('hidden lg:flex')}
        </div>

        <div className="mt-6 flex items-center justify-center gap-5 px-5" data-id="element-240" data-mp="2d6o76rfacx4h">
          {prevButton('lg:hidden')}
          <div className="flex items-center gap-1.5" role="group" aria-label="Choose a page" data-id="element-241" data-mp="23f1oka0mbvrp">
            {pages.map((page, i) => <button key={page.sample} type="button" onClick={() => interact(() => goTo(i))} aria-label={`Go to page ${i + 1}: ${page.caption}`} aria-current={pos.active === i ? 'true' : undefined} className="flex h-8 items-center justify-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hl" data-id="element-242" data-mp="2r5ntvtj8n1y0" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ${pos.active === i ? 'w-6 bg-hl' : 'w-2 bg-white/30'}`} data-id="element-243" data-mp="16r5frrbfa4r1" />
              </button>)}
          </div>
          {nextButton('lg:hidden')}
        </div>

        <p className="mt-6 px-5 text-center text-[13px] text-chalk/60" data-id="element-244" data-mp="33mgiaow9nhg2">Tap any page to see it full size.</p>

        <div className="mx-auto mt-8 max-w-md px-5" data-id="element-245" data-mp="2yn3wm7h4sgf2">
          <CtaButton data-id="element-246" data-mp="2ymauxkx14h3s">Get Track Coach Ready</CtaButton>
        </div>
      </div>

      <PageLightbox pages={pages} index={lightboxIndex} onIndexChange={changeLightbox} onClose={closeLightbox} data-id="element-247" data-mp="2unv415xtuol3" />
    </section>;
}